import { Box3, Group, Vector3 } from 'three';
import { EEntityType } from '../types';
import BaseEntity from './BaseEntity';
import { getUniqueId } from '../utils';
import { ENTITY_PRIORITY } from '../constant/entity';
import { EContainerType } from '../types/entity';
import { globalStores } from '../stores';
import { modelManager } from '../manager/entityManager';
import FurnitureEntity from './furnitureEntity';
import { IAssemblyData } from '../data/assembly/assembly';
import { getBox3Vertices, getRotatedRectangleVertices } from '../math/utils';
import { pixelRatio2d } from '../math/const';

class AssemblyEntity extends BaseEntity<EEntityType.Assembly> {
  protected _modelGroup: Group = new Group();
  private _entityId: string;
  private _accessorieIds: string[];

  constructor(params?: { id: string }) {
    super({ ...params, type: EEntityType.Assembly });

    const id = params?.id || getUniqueId();
    this._entityId = id;
    this._id = id;
    this._accessorieIds = [];

    this._modelGroup.userData.type = EEntityType.Assembly;
    this._modelGroup.userData.priority = ENTITY_PRIORITY.assembly;
    this._modelGroup.userData.entityId = id;
    this._modelGroup.userData.containerType = EContainerType.entity;
  }

  get group() {
    return this._modelGroup;
  }

  get entityId() {
    return this._entityId;
  }

  get accessorieIds() {
    return this._accessorieIds;
  }

  get data() {
    const data = globalStores.assemblyStore.getAssemblyData(this._entityId);
    return data;
  }

  override getBoundingBox(): Box3 {
    const box = new Box3();
    if (this.data) {
      const vertices = getRotatedRectangleVertices(
        this.data.width,
        this.data.thickness,
        this.data.center,
        this.data.rotation
      );
      vertices.forEach((point) => {
        box.expandByPoint(new Vector3(point.x ? point.x / pixelRatio2d : 0, point.y ? point.y / pixelRatio2d : 0, 0));
      });
    }
    return box;
  }

  override getBoundingBox3d(): Box3 {
    const box = new Box3();
    if (this.data) {
      const vertices = getBox3Vertices({
        width: this.data.width,
        thickness: this.data.thickness,
        height: this.data.height,
        center: this.data.center,
        rotation: this.data.rotation,
        offGround: this.data.offGround
      });
      vertices.forEach((vertex) => {
        box.expandByPoint(new Vector3(vertex.x / pixelRatio2d, vertex.y / pixelRatio2d, vertex.z / pixelRatio2d));
      });
    }
    return box;
  }

  setVisible(visible: boolean) {
    if (visible === this._visible) return;
    this._visible = visible;

    for (const accessorieId of this._accessorieIds) {
      const furnitureEntity = modelManager.getEntity(accessorieId, EEntityType.Furniture);
      if (furnitureEntity) {
        furnitureEntity.setVisible(visible);
      }
    }
  }

  override update(assemblyData: IAssemblyData) {
    this._accessorieIds = assemblyData.accessorieIds;
    for (const accessorieId of assemblyData.accessorieIds) {
      const furnitureData = globalStores.modelStore.getFurnitureData(accessorieId);
      if (furnitureData) {
        const entity = modelManager.furnitureManager.getEntity(accessorieId);
        if (entity) {
          entity.update(furnitureData);
        } else {
          const newEntity = new FurnitureEntity({
            id: accessorieId,
            visible: true
          });
          modelManager.furnitureManager.addEntity(newEntity);
          newEntity.update(furnitureData);
        }
      }
    }
  }

  clear() {}
}

export default AssemblyEntity;
